// react core
import { useContext, useEffect } from "react";

// pull to refresh
import PullToRefresh from "react-simple-pull-to-refresh";

// material design
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// entzy context and services
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { PullLoader, PullPrompt } from "components/utils/common/CommonLoaders";
import { UserAvatar } from "components/utils/common/CommonAvatars";

import { createMainMenuList } from "models/main";
import { scrollSnapTop } from "services/tools";

// entzy components
import AboutExperiment from "components/about/AboutExperiment";
import HomeQrCode from "components/home/HomeQrCode";

// entzy assets
import FallbackUserImage from "assets/banners/crowd-t50.png";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const homeMainMenuList = createMainMenuList().filter((item) => item.homePage);

function HomeUserBanner(props) {
  const navigate = props.navigate;
  const mainContext = useContext(MainContext);

  const handleRefresh = async () => {
    const response = await Promise.all([
      mainContext.preparePullNotifications({
        Category: null,
      }),
      mainContext.preparePullViewerEvents({
        nextToken: null,
      }),
    ]);
    if (response.filter((item) => item.alert).length > 0) {
      mainContext.updateAlert({
        alert: true,
        message:
          "Hmm unable to refresh content on that try. Give it another go or contact us if this problem persists.",
      });
      return true;
    } else {
      mainContext.pullNotifications(response[0]);
      mainContext.pullViewerEvents(response[1]);
      return true;
    }
  };

  const UserBannerMenuItem = (props) => {
    const item = props.item;
    return (
      <Box
        className="box-default text-center action-pointover"
        onClick={() => {
          mainContext.selectMenu(item.id);
          navigate(item.nav);
          scrollSnapTop();
        }}
      >
        <Typography
          variant="h3"
          color="white"
          sx={{
            mb: configEntzy.APP_SPACING_SM,
          }}
        >
          <span className="fa-layers fa-fw fa-2x">
            <FontAwesomeIcon
              icon={item.icon}
              transform="shrink-4"
              style={{
                color: "white",
                textShadow: "0 0 1px #ccc",
              }}
            />
            <span
              className={
                "fa-layers-counter" +
                (item.id === "messages"
                  ? " fa-layers-top-left"
                  : " fa-layers-top-right")
              }
              style={{
                backgroundColor: item.count > 0 ? "red" : "white",
                color: item.count > 0 ? "white" : "black",
                border: item.count > 0 ? "1px solid red" : "1px solid black",
                visibility: item.count === undefined ? "hidden" : "visible",
              }}
            >
              {item.count}
            </span>
          </span>
        </Typography>
        <Typography
          variant="subtitle2"
          color="white"
          sx={{
            mb: configEntzy.APP_SPACING_SM,
          }}
        >
          {item.name}
        </Typography>
      </Box>
    );
  };

  return (
    <Box className="box-default">
      <Box className="box-default relative">
        <Box
          className="box-default"
          sx={{
            // mb: configEntzy.APP_SPACING_LG,
            "&::before": {
              content: `''`,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage:
                "url(" +
                (props.user.avatar.hydrated && props.user.avatar.images.lg
                  ? props.user.avatar.images.lg.data
                  : FallbackUserImage) +
                ")",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              filter:
                props.user.avatar.hydrated && props.user.avatar.images.lg
                  ? "blur(1px)"
                  : "none",
              opacity: 1,
              zIndex: -1,
            },
          }}
        >
          <PullToRefresh
            onRefresh={handleRefresh}
            refreshingContent={<PullLoader />}
            pullingContent={<PullPrompt />}
          >
            <Box
              className="box-default"
              sx={{
                pt: configEntzy.APP_SPACING_MD,
                pb: configEntzy.APP_SPACING_LG,
              }}
            >
              <Container maxWidth="lg" disableGutters>
                <Box className="box-default" sx={{ width: "33%" }}>
                  <Box
                    className="box-default text-left"
                    sx={{
                      pt: configEntzy.APP_SPACING_MD2X,
                      pl: configEntzy.APP_SPACING_MD2X,
                    }}
                  >
                    {/* --- messaging menu button --- */}
                    <Box className="box-default">
                      {homeMainMenuList
                        .filter((item) => item.id === "messages")
                        .map((item, index) => {
                          item.count =
                            mainContext.state.notifications.data.messagesUnreadCount;
                          return (
                            <Box key={item.id} className="box-default">
                              <UserBannerMenuItem item={item} />
                            </Box>
                          );
                        })}
                    </Box>
                    {/* --- messaging menu button --- */}
                  </Box>
                </Box>
                <Box className="box-default" sx={{ width: "34%" }}>
                  <Box
                    className="box-inline action-pointer"
                    sx={{
                      pt: configEntzy.APP_SPACING_MD,
                    }}
                    onClick={
                      props.user.connected
                        ? () => props.menuSelectById("settings-profile")
                        : null
                    }
                  >
                    <UserAvatar user={props.user} size="md" avatarOnly />
                  </Box>
                </Box>
                <Box className="box-default" sx={{ width: "33%" }}>
                  <Box
                    className="box-default text-right"
                    sx={{
                      pt: configEntzy.APP_SPACING_MD2X,
                      pr: configEntzy.APP_SPACING_MD2X,
                    }}
                  >
                    {/* --- activity menu button --- */}
                    <Box className="box-default">
                      {homeMainMenuList
                        .filter((item) => item.id === "activity")
                        .map((item, index) => {
                          item.count =
                            mainContext.state.notifications.data.activityUnreadCount;
                          return (
                            <Box key={item.id} className="box-default">
                              <UserBannerMenuItem item={item} />
                            </Box>
                          );
                        })}
                    </Box>
                    {/* --- activity menu button --- */}
                  </Box>
                </Box>
                <Box className="box-default">
                  <Box
                    className="box-default"
                    sx={{
                      pt: configEntzy.APP_SPACING_MD,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="white"
                      className="glowing-text"
                    >
                      {"@" + props.user.name}
                    </Typography>
                  </Box>
                </Box>
              </Container>
            </Box>
          </PullToRefresh>
        </Box>
      </Box>
    </Box>
  );
}

function HomeMembers(props) {
  // const user = props.user;
  const navigate = props.navigate;

  const mainContext = useContext(MainContext);

  // effect to listen for menu source to see if user is returning to else where
  useEffect(() => {
    if (mainContext.state.viewer.trail.menuSourcePage) {
      mainContext.updateViewerTrail({
        menuSourcePage: null,
      });
      let redirectPage = mainContext.state.viewer.trail.menuSourcePage;
      navigate("/" + redirectPage);
    }
  }, [mainContext, navigate]);

  return (
    <Box className="box-default">
      <Box
        className="box-default"
        sx={{
          p: configEntzy.APP_SPACING_MD,
        }}
      >
        {/* --------- user avatar banner --------- */}
        <Box
          className="box-default nooverflow"
          sx={{
            borderRadius: configEntzy.BORDER_SIZE_XL,
          }}
        >
          <HomeUserBanner {...props} />
        </Box>

        <AboutExperiment {...props} loadPreview={true} />

        {/* --------- home content --------- */}
        <Box
          className="box-default"
          // sx={{
          //   mt: configEntzy.APP_SPACING_MD,
          // }}
        >
          <Container maxWidth="lg" disableGutters>
            <Box className="box-default">
              {homeMainMenuList
                .filter(
                  (item) => item.id !== "messages" && item.id !== "activity",
                )
                .map((item, index) => {
                  if (item.id === "messages") {
                    item.count =
                      mainContext.state.notifications.data.messagesUnreadCount;
                  }
                  if (item.id === "activity") {
                    item.count =
                      mainContext.state.notifications.data.activityUnreadCount;
                  }

                  return (
                    <Box
                      key={item.id}
                      className="box-default half-width"
                      sx={{
                        pr: index % 2 === 0 ? configEntzy.APP_SPACING_SM : 0,
                        pl: index % 2 === 1 ? configEntzy.APP_SPACING_SM : 0,
                        pb: configEntzy.APP_SPACING_SM2X,
                      }}
                    >
                      <Box
                        className="box-default"
                        sx={{
                          borderRadius: configEntzy.BORDER_SIZE_XL,
                          backgroundImage: item.bgImage
                            ? `url(${item.bgImage})`
                            : undefined,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          p: "1px",
                        }}
                      >
                        <Box
                          className="box-default bg-black-t90 shadow-default action-pointover"
                          sx={{
                            pt: configEntzy.APP_SPACING_LG,
                            pb: configEntzy.APP_SPACING_LG,
                            borderRadius: configEntzy.BORDER_SIZE_XL,
                          }}
                          onClick={() => {
                            mainContext.selectMenu(item.id);
                            navigate(item.nav);
                            scrollSnapTop();
                          }}
                        >
                          <Typography
                            variant="h3"
                            color="white"
                            sx={{
                              mb: configEntzy.APP_SPACING_SM,
                            }}
                          >
                            <span className="fa-layers fa-fw fa-2x">
                              <FontAwesomeIcon
                                icon={item.icon}
                                transform="shrink-4"
                                style={{
                                  color: "white",
                                  textShadow: "0 0 1px #ccc",
                                }}
                              />
                              <span
                                className={
                                  "fa-layers-counter" +
                                  (item.id === "messages"
                                    ? " fa-layers-top-left"
                                    : " fa-layers-top-right")
                                }
                                style={{
                                  backgroundColor:
                                    item.count > 0 ? "red" : "white",
                                  color: item.count > 0 ? "white" : "black",
                                  border:
                                    item.count > 0
                                      ? "1px solid red"
                                      : "1px solid black",
                                  visibility:
                                    item.count === undefined
                                      ? "hidden"
                                      : "visible",
                                }}
                              >
                                {item.count}
                              </span>
                            </span>
                          </Typography>
                          <Typography
                            variant="h6"
                            color="white"
                            sx={{
                              mb: configEntzy.APP_SPACING_SM,
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            color="white"
                            sx={{
                              mb: configEntzy.APP_SPACING_SM,
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
            <Box
              className="box-default"
              sx={{
                mt: configEntzy.APP_SPACING_LG,
                mb: configEntzy.APP_SPACING_LG,
              }}
            >
              <HomeQrCode {...props} />
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

export default HomeMembers;
